/**
 * The notifications bell.
 *
 * NOT WIRED INTO THE SHELL. It is exported for the app shell to place; see the
 * hand-over note in the task report for the two lines to add to Shell.tsx.
 *
 * WHAT IT POLLS AND WHY THAT IS THE CHEAP CALL
 * Only the unread COUNT polls (every 60s, and never while the tab is hidden).
 * The list is fetched when the panel is opened and not before. That ratio
 * matters: the count is one indexed query against `[userId, readAt]`, bounded
 * to 100 rows by the server, whereas the list joins nothing but returns real
 * payloads. Polling the list instead would multiply the traffic for information
 * nobody is looking at.
 *
 * TEXT IS RENDERED, NOT STORED
 * The server sends a machine `type` and a parameter bag. This component renders
 * `notification.type.<TYPE>` with those parameters, so a notification raised by
 * last night's job reads in whatever language the viewer has chosen today. Any
 * unknown type falls back to a generic sentence rather than rendering a raw key
 * — a new notification type shipped by the API ahead of the web client must
 * degrade, not break.
 *
 * ACCESSIBILITY
 * The trigger is a real button with `aria-expanded` and `aria-haspopup`; the
 * panel closes on Escape and on an outside click, and returns focus to the
 * trigger. The unread count is in the accessible name, not only in the badge,
 * because a coloured dot says nothing to a screen reader.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UNREAD_NOTIFICATION_CAP } from '@opencomply/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import { Button, Spinner, ErrorNotice } from './ui';

// ---------------------------------------------------------------------------
//  Strings
//
//  English inline, so the bell works before the locale files are updated.
//  `{{x}}` is i18next interpolation. Keys ending `_one`/`_other` are PLURALS
//  selected by the `count` variable — a flat key would render raw.
// ---------------------------------------------------------------------------

export const NOTIFICATION_STRINGS = {
  'notification.title': 'Notifications',
  'notification.open_one': 'Notifications, {{count}} unread',
  'notification.open_other': 'Notifications, {{count}} unread',
  'notification.openNone': 'Notifications, none unread',
  'notification.empty': 'Nothing to report.',
  'notification.emptyUnread': 'You are up to date.',
  'notification.loadFailed': 'Could not load your notifications.',
  'notification.markAllRead': 'Mark all as read',
  'notification.markRead': 'Mark as read',
  'notification.showAll': 'Show all',
  'notification.showUnread': 'Unread only',
  'notification.loadMore': 'Load more',
  'notification.unreadDot': 'Unread',
  'notification.view': 'Open',
  'notification.countCapped': '{{count}}+',

  // --- One line per notification type ---
  // The parameter names are the ones the server puts in `paramsJson`; renaming
  // one here without renaming it there renders an empty gap in the sentence.
  'notification.type.EVIDENCE_EXPIRING_SOON_one':
    'Evidence “{{evidence_name}}” expires in {{count}} day.',
  'notification.type.EVIDENCE_EXPIRING_SOON_other':
    'Evidence “{{evidence_name}}” expires in {{count}} days.',
  'notification.type.EVIDENCE_EXPIRED_one':
    'Evidence “{{evidence_name}}” expired {{count}} day ago. Anything relying on it is no longer supported.',
  'notification.type.EVIDENCE_EXPIRED_other':
    'Evidence “{{evidence_name}}” expired {{count}} days ago. Anything relying on it is no longer supported.',
  'notification.type.REMEDIATION_PLAN_ASSIGNED':
    'You were assigned the remediation plan “{{plan_name}}”.',
  'notification.type.REMEDIATION_PLAN_AT_RISK':
    'The remediation plan “{{plan_name}}” is approaching its due date.',
  'notification.type.REMEDIATION_PLAN_OVERDUE':
    'The remediation plan “{{plan_name}}” is overdue.',
  'notification.type.POLICY_APPROVAL_ASSIGNED':
    'You were asked to approve the policy “{{policy_name}}”.',
  'notification.type.SCAN_ORG_REPORT_READY': 'A security scan report is ready.',
  'notification.type.SCAN_VENDOR_REPORT_READY':
    'A security scan report for {{vendor_name}} is ready.',
  'notification.type.SCAN_ORG_SCORE_CHANGED': 'Your security scan score has changed.',
  'notification.type.SCAN_VENDOR_SCORE_CHANGED':
    'The security scan score for {{vendor_name}} has changed.',
  'notification.type.LICENCE_PLAN_CHANGED': 'Your plan has changed.',
  'notification.type.LICENCE_EXPIRY_REMINDER_30': 'Your plan expires in 30 days.',
  'notification.type.LICENCE_EXPIRY_REMINDER_7': 'Your plan expires in 7 days.',
  'notification.type.LICENCE_EXPIRY_REMINDER_1': 'Your plan expires tomorrow.',
  'notification.type.TPRM_VENDOR_RESPONDED':
    '{{vendor_name}} has answered their assessment.',
  'notification.type.MANAGER_JOINED': '{{organization_name}} joined to manage this organisation.',
  /** Fallback for a type this build of the client does not know about. */
  'notification.type.UNKNOWN': 'You have a new notification.',
} as const;

export type NotificationKey = keyof typeof NOTIFICATION_STRINGS;

/** Plural keys, by their BASE name — what the code passes to `tn()`. */
type NotificationPluralKey =
  | 'notification.open'
  | 'notification.type.EVIDENCE_EXPIRING_SOON'
  | 'notification.type.EVIDENCE_EXPIRED';

/**
 * The inline English for a key, picking the right plural form.
 *
 * i18next never looks up a bare key when `count` is passed — it looks up
 * `<key>_one` / `<key>_other`. A single flat default would therefore be reused
 * for both forms and ship "1 days" to a customer.
 */
function englishDefault(key: string, count: unknown): string | undefined {
  const table = NOTIFICATION_STRINGS as Record<string, string | undefined>;
  const direct = table[key];
  if (direct !== undefined) return direct;
  if (typeof count === 'number') {
    return table[`${key}${count === 1 ? '_one' : '_other'}`];
  }
  return undefined;
}

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface NotificationItem {
  id: string;
  type: string;
  params: Record<string, unknown>;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationList {
  items: NotificationItem[];
  nextCursor: string | null;
}

interface UnreadCount {
  count: number;
  capped: boolean;
}

// ---------------------------------------------------------------------------
//  Component
// ---------------------------------------------------------------------------

export function NotificationBell({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const { allows, session } = useAuth();
  const queryClient = useQueryClient();
  const describeApiError = useApiError();

  const tn = useCallback(
    (key: NotificationKey | NotificationPluralKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: englishDefault(key, options?.count), ...options }),
    [t],
  );

  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [collected, setCollected] = useState<NotificationItem[]>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * Keyed by organisation.
   *
   * Switching organisation re-issues the session token server-side and the API
   * then answers for the new tenant. Without the organisation in the key, the
   * previous tenant's cached notifications would stay on screen until the
   * refetch landed — one tenant's record names visible inside another's
   * session, which is the exact leak the server-side scoping exists to prevent.
   */
  const orgId = session?.organization.id ?? 'none';
  const canRead = allows('notification.read');
  const canWrite = allows('notification.update');

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count', orgId],
    queryFn: () => api.get<UnreadCount>('/notifications/unread-count'),
    enabled: canRead,
    refetchInterval: 60_000,
    // The default would keep polling a hidden tab. A background tab's badge is
    // refreshed the moment it is focused, which is the only moment it matters.
    refetchIntervalInBackground: false,
  });

  const list = useQuery({
    queryKey: ['notifications', 'list', orgId, unreadOnly, cursor],
    queryFn: () =>
      api.get<NotificationList>(
        `/notifications?${new URLSearchParams({
          ...(unreadOnly ? { unread: 'true' } : {}),
          ...(cursor ? { cursor } : {}),
        }).toString()}`,
      ),
    // Fetched on open, not before.
    enabled: canRead && open,
  });

  // Pages accumulate so "load more" appends rather than replacing.
  useEffect(() => {
    if (!list.data) return;
    setCollected((current) =>
      cursor === null
        ? list.data.items
        : // Guard against a page arriving twice (a refetch while paging):
          // ids already held are not appended again.
          [...current, ...list.data.items.filter((i) => !current.some((c) => c.id === i.id))],
    );
  }, [list.data, cursor]);

  const resetPaging = () => {
    setCursor(null);
    setCollected([]);
  };

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: (_result, id) => {
      // Patched locally as well as invalidated: the row is already on screen
      // and should stop looking unread immediately, not after a round trip.
      setCollected((current) =>
        current.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', orgId] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post<{ marked: number }>('/notifications/read-all'),
    onSuccess: () => {
      const now = new Date().toISOString();
      setCollected((current) => current.map((n) => ({ ...n, readAt: n.readAt ?? now })));
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // --- Panel placement -------------------------------------------------------

  /**
   * WHY THE PANEL IS PORTALLED AND MEASURED, RATHER THAN `absolute right-0`.
   *
   * This component is designed to be dropped into the app shell, and both of
   * that shell's bars use `backdrop-blur`. A `backdrop-filter` establishes a
   * containing block for FIXED descendants, so a `position: fixed` panel
   * rendered inside the bar is positioned against the BAR, not the viewport —
   * a bottom sheet ends up pinned to the bottom of a 56px-tall header. An
   * `absolute right-0` dropdown has the mirror problem: it is anchored to the
   * button, so wherever the bell is not at the right edge, a 22rem panel runs
   * off the side of a 375px screen.
   *
   * Portalling to `document.body` escapes both, and measuring the trigger puts
   * the panel under the bell wherever the shell decides to put it. The clamps
   * below are what keep it on screen at any width.
   */
  const [anchor, setAnchor] = useState<{ top: number; right: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;

    const measure = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const GUTTER = 8;
      const PREFERRED = 352; // 22rem
      // The width is computed here rather than left to CSS so the clamp below
      // can reason about it. A CSS `min()` would leave this calculation
      // guessing at the real width and getting it wrong at small sizes.
      const width = Math.min(PREFERRED, window.innerWidth - GUTTER * 2);

      // Right-aligned to the bell, but clamped at BOTH edges. The upper bound
      // is what stops a 352px panel running off the left of a 375px screen
      // when the bell is not at the right edge — which is entirely up to
      // whoever placed this component, and not something it can assume.
      const rightMax = Math.max(GUTTER, window.innerWidth - width - GUTTER);
      const right = Math.min(Math.max(GUTTER, window.innerWidth - rect.right), rightMax);

      setAnchor({ top: rect.bottom + GUTTER, right, width });
    };

    measure();
    window.addEventListener('resize', measure);
    // Capture phase: the trigger may sit inside a scrolling container whose
    // scroll events do not bubble to window.
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  // --- Dismissal ------------------------------------------------------------

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Focus must come back to the trigger, or a keyboard user is dropped at
      // the top of the document.
      triggerRef.current?.focus();
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  // Hidden entirely for anyone who may not read notifications. Courtesy, not
  // security: the server refuses the routes regardless.
  if (!canRead) return null;

  const unreadTotal = unread.data?.count ?? 0;
  const capped = unread.data?.capped ?? false;
  const badge = capped ? tn('notification.countCapped', { count: UNREAD_NOTIFICATION_CAP }) : String(unreadTotal);

  const fmt = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );

  /**
   * One notification's sentence.
   *
   * `count` is pulled out of the parameter bag explicitly because i18next needs
   * it as a top-level option to select the plural form — buried inside the rest
   * of the spread it still works, but only by accident of object order, and the
   * next person to touch this should not have to know that.
   */
  const describe = (item: NotificationItem): string => {
    const key = `notification.type.${item.type}` as NotificationKey;
    const known =
      key in NOTIFICATION_STRINGS ||
      `${key}_other` in (NOTIFICATION_STRINGS as Record<string, unknown>);
    if (!known) return tn('notification.type.UNKNOWN');

    const { count, ...rest } = item.params as { count?: unknown };
    return tn(key as NotificationKey, {
      ...rest,
      ...(typeof count === 'number' ? { count } : {}),
    });
  };

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) resetPaging();
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        // The count is in the accessible NAME, not only in the badge: a
        // coloured dot tells a screen-reader user nothing.
        aria-label={
          unreadTotal > 0
            ? tn('notification.open', { count: unreadTotal })
            : tn('notification.openNone')
        }
        className="relative p-2 rounded-lg text-ink-secondary hover:bg-surface-sunken
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
      >
        <BellGlyph />
        {unreadTotal > 0 && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1
                       rounded-full bg-critical text-white text-[10px] font-semibold
                       grid place-items-center tabular-nums"
          >
            {badge}
          </span>
        )}
      </button>

      {open && anchor && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label={tn('notification.title')}
          style={{
            top: anchor.top,
            right: anchor.right,
            width: anchor.width,
            // Height is whatever is left below the trigger, so a long list
            // scrolls inside the panel instead of running off the bottom.
            maxHeight: `min(28rem, calc(100vh - ${anchor.top}px - 1rem))`,
          }}
          className="fixed z-50 rounded-2xl border border-line bg-surface-raised
                     shadow-lg flex flex-col animate-fade-in"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-line">
            <p className="text-sm font-semibold text-ink">{tn('notification.title')}</p>
            <button
              type="button"
              className="text-xs font-medium text-brand hover:underline py-1"
              onClick={() => {
                setUnreadOnly((v) => !v);
                resetPaging();
              }}
            >
              {unreadOnly ? tn('notification.showAll') : tn('notification.showUnread')}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {list.isLoading && collected.length === 0 ? (
              <div className="p-6 grid place-items-center">
                <Spinner className="w-5 h-5" />
              </div>
            ) : list.isError ? (
              <div className="p-3">
                <ErrorNotice
                  message={describeApiError(list.error)}
                  onRetry={() => void list.refetch()}
                  retryLabel={t('common.retry')}
                />
              </div>
            ) : collected.length === 0 ? (
              <p className="p-6 text-sm text-ink-muted text-center">
                {unreadOnly ? tn('notification.emptyUnread') : tn('notification.empty')}
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {collected.map((item) => (
                  <li
                    key={item.id}
                    className={cn('px-3 py-2.5', !item.readAt && 'bg-brand/[0.04]')}
                  >
                    <div className="flex items-start gap-2">
                      {!item.readAt && (
                        <span
                          className="mt-1.5 w-2 h-2 rounded-full bg-brand shrink-0"
                          // Text, not just a dot: the unread state must survive
                          // greyscale and a screen reader.
                          role="img"
                          aria-label={tn('notification.unreadDot')}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink leading-snug break-words">
                          {describe(item)}
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-muted">
                          <time dateTime={item.createdAt}>{fmt(item.createdAt)}</time>
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          {item.link && (
                            <Link
                              to={item.link}
                              onClick={() => {
                                setOpen(false);
                                // Following a notification is reading it.
                                if (canWrite && !item.readAt) markRead.mutate(item.id);
                              }}
                              className="text-xs font-medium text-brand hover:underline"
                            >
                              {tn('notification.view')}
                            </Link>
                          )}
                          {canWrite && !item.readAt && (
                            <button
                              type="button"
                              className="text-xs text-ink-muted hover:text-ink-secondary hover:underline"
                              onClick={() => markRead.mutate(item.id)}
                              disabled={markRead.isPending}
                            >
                              {tn('notification.markRead')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(list.data?.nextCursor || (canWrite && unreadTotal > 0)) && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-line">
              {canWrite && unreadTotal > 0 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={markAllRead.isPending}
                  onClick={() => markAllRead.mutate()}
                >
                  {tn('notification.markAllRead')}
                </Button>
              ) : (
                <span />
              )}
              {list.data?.nextCursor && (
                <Button
                  size="sm"
                  disabled={list.isFetching}
                  onClick={() => setCursor(list.data?.nextCursor ?? null)}
                >
                  {tn('notification.loadMore')}
                </Button>
              )}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

// --- Glyph (kept local: used only here) --------------------------------------

const BellGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="w-5 h-5"
  >
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
